import { Navigate, useLocation } from 'react-router-dom'
import { useAuth, useFamily, usePendingInvitations } from '@/hooks'
import { LoadingSpinner } from './LoadingSpinner'

interface ProtectedRouteProps {
  children: React.ReactNode
  /** Skip the onboarding check - used for onboarding routes themselves */
  skipOnboardingCheck?: boolean
}

export function ProtectedRoute({ children, skipOnboardingCheck = false }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const { confirmedNoFamilies, isLoading: isFamiliesLoading } = useFamily()
  const { invitations, isLoading: isInvitationsLoading } = usePendingInvitations()
  const location = useLocation()

  const isLoading = isAuthLoading || isFamiliesLoading || isInvitationsLoading

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Only redirect to onboarding if:
  // 1. Query has SUCCESSFULLY completed (not loading, not error)
  // 2. AND returned empty data
  // confirmedNoFamilies is ONLY true when isSuccess && no families in store or query
  if (!skipOnboardingCheck && confirmedNoFamilies) {
    // If they have pending invitations, send to invitations page
    if (invitations.length > 0) {
      return <Navigate to="/onboarding/invitations" replace />
    }
    // Otherwise, send to main onboarding
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

// Route wrapper for onboarding pages that shouldn't redirect to onboarding
export function OnboardingRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute skipOnboardingCheck>{children}</ProtectedRoute>
}
